import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Flag,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Ban,
  Trash2,
  Eye,
  Clock,
  Filter,
  RefreshCw
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  reviewed: { label: 'Revisto', color: 'bg-blue-500/20 text-blue-400', icon: Eye },
  dismissed: { label: 'Dispensado', color: 'bg-gray-500/20 text-gray-400', icon: XCircle },
  action_taken: { label: 'Acção Tomada', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
};

const CONTENT_TYPE_LABELS = {
  story: 'Conto',
  comment: 'Comentário',
  confession: 'Confissão',
  article: 'Artigo',
};

export default function AdminReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterType, setFilterType] = useState('all');
  
  // Action modal
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/home');
      return;
    }
    fetchData();
  }, [user, filterStatus, filterType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        api.get('/admin/reports', {
          params: {
            status: filterStatus !== 'all' ? filterStatus : undefined,
            content_type: filterType !== 'all' ? filterType : undefined,
            limit: 100
          }
        }),
        api.get('/admin/reports/stats')
      ]);
      setReports(reportsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (report) => {
    setSelectedReport(report);
    setSelectedAction('');
    setAdminNotes('');
    setActionModalOpen(true);
  };

  const handleAction = async () => {
    if (!selectedAction || !selectedReport) return;

    setActionLoading(true);
    try {
      await api.post(`/admin/reports/${selectedReport.id}/action`, {
        action: selectedAction,
        admin_notes: adminNotes || null
      });

      toast.success('Acção executada com sucesso');
      setActionModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao executar acção'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Flag className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">
                  Gestão de Relatórios
                </h1>
                <p className="text-muted-foreground">
                  Moderar conteúdo reportado pelos utilizadores
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-border"
              data-testid="refresh-reports"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-surface border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.pending}</p>
                      <p className="text-xs text-muted-foreground">Pendentes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-surface border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.action_taken}</p>
                      <p className="text-xs text-muted-foreground">Acções</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-surface border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-500/10 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.dismissed}</p>
                      <p className="text-xs text-muted-foreground">Dispensados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-surface border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-ruby/10 flex items-center justify-center">
                      <Flag className="w-5 h-5 text-ruby" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px] bg-surface border-border">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="action_taken">Acção Tomada</SelectItem>
                  <SelectItem value="dismissed">Dispensados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px] bg-surface border-border">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-border">
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="story">Contos</SelectItem>
                <SelectItem value="comment">Comentários</SelectItem>
                <SelectItem value="confession">Confissões</SelectItem>
                <SelectItem value="article">Artigos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reports List */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-ruby" />
            </div>
          ) : reports.length > 0 ? (
            <div className="space-y-4">
              {reports.map((report) => {
                const StatusIcon = STATUS_CONFIG[report.status]?.icon || Clock;
                return (
                  <Card 
                    key={report.id} 
                    className="bg-surface border-border"
                    data-testid={`report-card-${report.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Content Info */}
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge className={STATUS_CONFIG[report.status]?.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {STATUS_CONFIG[report.status]?.label}
                            </Badge>
                            <Badge variant="outline" className="border-border">
                              {CONTENT_TYPE_LABELS[report.content_type]}
                            </Badge>
                            <Badge className="bg-red-500/20 text-red-400">
                              {report.reason_label}
                            </Badge>
                          </div>
                          
                          {report.content_title && (
                            <h3 className="font-semibold text-foreground mb-1">
                              {report.content_title}
                            </h3>
                          )}
                          
                          {report.content_preview && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {report.content_preview}
                            </p>
                          )}
                          
                          {report.description && (
                            <div className="mt-2 p-2 rounded bg-surface-light">
                              <p className="text-xs text-muted-foreground mb-1">Descrição do relatório:</p>
                              <p className="text-sm text-foreground">{report.description}</p>
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                            <span>Reportado por: <strong>{report.reporter_name}</strong></span>
                            {report.content_author_name && (
                              <span>Autor: <strong>{report.content_author_name}</strong></span>
                            )}
                            <span>{formatDate(report.created_at)}</span>
                          </div>
                          
                          {report.action_taken && (
                            <div className="mt-2 p-2 rounded bg-green-500/10 border border-green-500/20">
                              <p className="text-xs text-green-400">
                                <strong>Acção:</strong> {report.action_taken}
                                {report.reviewed_by && ` por ${report.reviewed_by}`}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        {report.status === 'pending' && (
                          <div className="flex gap-2 md:flex-col">
                            <Button
                              size="sm"
                              onClick={() => openActionModal(report)}
                              className="bg-ruby hover:bg-ruby-light"
                              data-testid={`report-action-${report.id}`}
                            >
                              Tomar Acção
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-surface flex items-center justify-center mx-auto mb-6">
                <Flag className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Nenhum relatório encontrado
              </h2>
              <p className="text-muted-foreground">
                {filterStatus !== 'all' || filterType !== 'all' 
                  ? 'Tenta ajustar os filtros'
                  : 'Ainda não há relatórios de conteúdo'}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Action Modal */}
      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent className="bg-surface border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Tomar Acção</DialogTitle>
            <DialogDescription>
              Escolhe uma acção para este relatório
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={selectedAction === 'dismiss' ? 'default' : 'outline'}
                onClick={() => setSelectedAction('dismiss')}
                className={selectedAction === 'dismiss' ? 'bg-gray-600' : 'border-border'}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Dispensar
              </Button>
              <Button
                variant={selectedAction === 'warn' ? 'default' : 'outline'}
                onClick={() => setSelectedAction('warn')}
                className={selectedAction === 'warn' ? 'bg-amber-600' : 'border-border'}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Avisar
              </Button>
              <Button
                variant={selectedAction === 'remove' ? 'default' : 'outline'}
                onClick={() => setSelectedAction('remove')}
                className={selectedAction === 'remove' ? 'bg-red-600' : 'border-border'}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remover
              </Button>
              <Button
                variant={selectedAction === 'ban' ? 'default' : 'outline'}
                onClick={() => setSelectedAction('ban')}
                className={selectedAction === 'ban' ? 'bg-red-800' : 'border-border'}
              >
                <Ban className="w-4 h-4 mr-2" />
                Banir
              </Button>
            </div>

            <div className="p-3 rounded-lg bg-surface-light text-sm">
              {selectedAction === 'dismiss' && (
                <p className="text-muted-foreground">
                  O relatório será marcado como dispensado. Nenhuma acção será tomada contra o conteúdo.
                </p>
              )}
              {selectedAction === 'warn' && (
                <p className="text-amber-400">
                  O autor do conteúdo receberá um aviso. O conteúdo permanecerá visível.
                </p>
              )}
              {selectedAction === 'remove' && (
                <p className="text-red-400">
                  O conteúdo será removido da plataforma.
                </p>
              )}
              {selectedAction === 'ban' && (
                <p className="text-red-400 font-semibold">
                  O autor será banido permanentemente da plataforma.
                </p>
              )}
            </div>

            <Textarea
              placeholder="Notas administrativas (opcional)..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="bg-surface-light border-border text-foreground"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionModalOpen(false)}
              className="border-border"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAction}
              disabled={!selectedAction || actionLoading}
              className="bg-ruby hover:bg-ruby-light"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Confirmar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
